<div class="phone-input-container relative w-full">
  <mat-form-field [appearance]="appearance" class="w-full">
    <div class="flex items-center w-full">
      <!-- Country Selector -->
      <div class="relative inline-block flex-shrink-0">
        <button 
          type="button" 
          class="flex items-center gap-2 px-3 py-2 bg-transparent border-none border-r border-gray-300 cursor-pointer outline-none transition-all duration-200 min-w-fit h-full hover:bg-gray-100 active:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-gray-50"
          [disabled]="disabled"
          (click)="toggleDropdown()"
          tabindex="-1">
          @if (useFlagImage(selectedCountry.code)) {
            <img
              class="h-5 w-7 object-cover rounded-sm border border-gray-200"
              [src]="getFlagSrc(selectedCountry.code)"
              [alt]="selectedCountry.name"
              [title]="selectedCountry.name"
              (error)="onFlagError(selectedCountry.code)"
              loading="lazy"
            />
          } @else {
            <span class="text-2xl leading-none" [title]="selectedCountry.name">{{ selectedCountry.flag }}</span>
          }
          @if (separateDialCode) {
            <span class="text-sm font-medium font-mono text-gray-900">{{ selectedCountry.dialCode }}</span>
          }
          <mat-icon class="text-gray-500 text-xs">arrow_drop_down</mat-icon>
        </button>
        
        <!-- Country Dropdown -->
        @if (isDropdownOpen) {
          <div class="absolute top-full left-0 mt-1 w-80 bg-white shadow-lg rounded-lg border border-gray-200 z-50 max-h-96 overflow-hidden animate-fadeIn" (click)="$event.stopPropagation()">
            @if (enableSearch) {
              <div class="p-2 border-b border-gray-200">
                <div class="relative">
                  <mat-icon class="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">search</mat-icon>
                  <input 
                    type="text" 
                    #countrySearchInput
                    class="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Search countries..."
                    [(ngModel)]="searchTerm"
                    (input)="filterCountries()"
                    (click)="$event.stopPropagation()"
                    (keydown)="$event.stopPropagation()"
                    (keydown.escape)="isDropdownOpen = false"
                    autocomplete="off"
                  />
                </div>
              </div>
            }
            <div class="overflow-y-auto max-h-80" style="overscroll-behavior: contain;" (wheel)="$event.stopPropagation()">
              @for (country of filteredCountries; track country.code) {
                <div 
                  class="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 cursor-pointer transition-colors duration-150"
                  [class.bg-blue-50]="country.code === selectedCountry.code"
                  [class.font-semibold]="country.code === selectedCountry.code"
                  [class.border-l-4]="country.code === selectedCountry.code"
                  [class.border-blue-500]="country.code === selectedCountry.code"
                  (click)="selectCountry(country); $event.stopPropagation()"
                  role="option"
                  [attr.aria-selected]="country.code === selectedCountry.code"
                >
                  @if (useFlagImage(country.code)) {
                    <img
                      class="h-5 w-7 object-cover rounded-sm border border-gray-200 flex-shrink-0"
                      [src]="getFlagSrc(country.code)"
                      [alt]="country.name"
                      (error)="onFlagError(country.code)"
                      loading="lazy"
                    />
                  } @else {
                    <span class="text-2xl leading-none">{{ country.flag }}</span>
                  }
                  <span class="text-sm flex-1">{{ country.name }}</span>
                  <span class="text-sm text-gray-600 font-mono">{{ country.dialCode }}</span>
                  @if (country.code === selectedCountry.code) {
                    <mat-icon class="text-blue-600 text-sm">check</mat-icon>
                  }
                </div>
              }
              @if (filteredCountries.length === 0) {
                <div class="px-3 py-8 text-center">
                  <mat-icon class="text-gray-400 text-4xl mb-2">search_off</mat-icon>
                  <p class="text-gray-500 text-sm">No countries found</p>
                  <p class="text-gray-400 text-xs mt-1">Try a different search term</p>
                </div>
              }
            </div>
          </div>
        }
      </div>
      
      <!-- Phone Number Input -->
      <input 
        #phoneInput
        matInput
        type="tel"
        class="flex-1 pl-3 font-mono text-base"
        [placeholder]="placeholder || exampleNumber"
        [value]="phoneNumber"
        (input)="onPhoneNumberInput($event)"
        (paste)="onPhoneNumberPaste($event)"
        (blur)="handleBlur()"
        (focus)="handleFocus()"
        [disabled]="disabled"
        [required]="required"
        [maxlength]="maxLength"
        autocomplete="tel"
        [attr.aria-label]="label"
        [attr.aria-required]="required"
        [attr.aria-invalid]="hasInvalidNumber()"
      />
    </div>
  </mat-form-field>
   <!-- Error Messages -->
    @if (shouldShowErrors()) {
      @if (hasRequiredError()) {
        <mat-error class="text-xs">This field is required.</mat-error>
      }
      @if (hasInvalidNumber()) {
        <mat-error class="text-xs">Please enter a valid phone number for {{ selectedCountry.name }}.</mat-error>
      }
    }
    
    @if (phoneNumber && !hasInvalidNumber() && getNumberType()) {
      <mat-hint class="text-xs">{{ getNumberType() }} • {{ getFormattedNumber(1) }}</mat-hint>
    }
</div>

<!-- Tailwind Animation -->
<style>
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.15s ease-out;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .phone-input-container .absolute.w-80 {
      width: calc(100vw - 2rem);
      left: 50%;
      transform: translateX(-50%);
      max-width: 20rem;
    }
  }
</style>

